/**
 * House Rules chooser (#232, mockups 13 + 18) — the create-time experience that lets a
 * host pick a preset OR open an Advanced drawer to set every per-game option.
 *
 * Two preset cards — **Classic** (default) and **🎉 Game Night** — bundle the toggles;
 * the Advanced drawer exposes the individual switches grouped as Power-ups / Buzzer /
 * Scoring / **Timers / Wagers / Board** (#329) / Hosting. Presets simply SET the toggles —
 * there is no separate "mode": changing any switch just reshapes the toggle set, and the
 * selection label flips to **Custom** when the set matches neither preset. The Timers /
 * Wagers / Board groups (#329) expose the remaining per-game gameplay knobs — including a
 * per-game **Hidden Peril count** applied by relabeling arbitrary clues at game creation
 * (no regeneration).
 *
 * The component owns the toggle state and reports the RESOLVED, bounded
 * {@link HouseRules} up via `onChange` — `undefined` when the toggles equal Classic, so
 * a default create sends no `houseRules` and stays byte-for-byte unchanged. The server
 * is the authority: it re-validates + clamps whatever is sent.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import {
  MAX_SAVED_RULE_PRESETS,
  RULE_PRESET_NAME_MAX_LEN,
  type EarlyBuzzPolicy,
  type HostMode,
  type HouseRules,
  type SavedRulePreset,
} from '@jeopardy/shared';

/**
 * On-brand toggle switch (#315) — a visually-replaced checkbox for the drawer's boolean
 * rows. The real `<input>` stays in the DOM (label association, keyboard, and `disabled`
 * semantics intact); the pill + knob are drawn by `.hr-switch__track`, which the input
 * drives via the sibling selector. Same accessibility contract as the host-mode radios.
 */
function ToggleSwitch({
  testid,
  checked,
  disabled,
  onChange,
}: {
  testid: string;
  checked: boolean;
  disabled?: boolean;
  onChange: (checked: boolean) => void;
}): React.JSX.Element {
  return (
    <span className="hr-switch">
      <input
        type="checkbox"
        data-testid={testid}
        checked={checked}
        disabled={disabled}
        onChange={(e) => onChange(e.target.checked)}
      />
      <span className="hr-switch__track" aria-hidden="true" />
    </span>
  );
}

/**
 * A labeled numeric input row (#329) — the number analogue of {@link ToggleSwitch}, reusing
 * the shipped `house-rules__row` / `house-rules__hint` markup (no new CSS). Clamps to
 * `[min, max]` on change, mirroring the existing Auto-awarded / Starting-cards inputs.
 */
function NumberRow({
  label,
  hint,
  testid,
  value,
  min,
  max,
  onChange,
}: {
  label: string;
  hint: string;
  testid: string;
  value: number;
  min: number;
  max: number;
  onChange: (value: number) => void;
}): React.JSX.Element {
  return (
    <label className="house-rules__row">
      <span>
        {label}
        <span className="muted house-rules__hint"> — {hint}</span>
      </span>
      <input
        type="number"
        min={min}
        max={max}
        data-testid={testid}
        value={value}
        onChange={(e) => {
          const n = Number(e.target.value);
          onChange(
            Number.isFinite(n) ? Math.min(max, Math.max(min, Math.floor(n))) : value,
          );
        }}
      />
    </label>
  );
}

/** The individual toggle set behind the presets (client-only view model). */
interface RulesState {
  powerupCards: boolean;
  comebackBoosts: boolean;
  /** #232 v2 (F7): total comeback cards auto-granted to trailing players over the game. */
  autoAwardCount: number;
  /** #232 v2 (item 6): how many random distinct cards each player starts holding. */
  startingCardCount: number;
  earlyBuzzPolicy: EarlyBuzzPolicy;
  earlyBuzzSmashLimit: number;
  allowNegativeScores: boolean;
  // #329 Timers.
  defaultTimerSec: number;
  answerWindowSec: number;
  finalJeopardyWagerSec: number;
  finalJeopardyAnswerSec: number;
  // #329 Wagers.
  dailyDoubleMinWager: number;
  dailyDoubleMaxFloor: number;
  finalJeopardyMinWager: number;
  // #329 Board — per-round Hidden Peril count.
  dailyDoubleCountJeopardy: number;
  dailyDoubleCountDoubleJeopardy: number;
}

/** F7 bound — matches the server clamp in resolveHouseRules. */
const AUTO_AWARD_MAX = 6;
/** Starting-cards-per-player bound (item 6) — matches the server clamp + the 8-card deck. */
const STARTING_CARDS_MAX = 8;
/** Game Night default starting-card count (mirrors GAME_NIGHT_POWERUP_CONFIG). */
const DEFAULT_STARTING_CARDS = 4;

/**
 * #329 clamp ranges — MIRROR the server `CLAMP`/`DD_COUNT_MAX` in `house-rules.ts`
 * exactly (the server re-validates, so these keep the input UX honest, not the authority).
 */
const TIMER_MIN = 3;
const TIMER_MAX = 120;
const FINAL_SEC_MAX = 600;
const FINAL_ANSWER_MIN = 5;
const WAGER_MAX = 100_000;
/** Max Hidden Perils per round = clues per board (6×5 = 30), per D-329.3. */
const DD_COUNT_MAX = 30;

/**
 * #329 defaults shown in the Timers/Wagers/Board inputs. The seven timer/wager values are
 * the real `DEFAULT_GAME_RULES` engine defaults; the two Board counts are the illustrative
 * `PACK_RULES` structural values (1 in the Peril round, 2 in the Double Peril round). Both
 * bundles carry them at these defaults so an untouched drawer stays Classic (sends nothing);
 * a change to any flips the label to Custom exactly like the existing switches (D-329.8).
 */
const CONFIG_DEFAULTS = {
  defaultTimerSec: 15,
  answerWindowSec: 15,
  finalJeopardyWagerSec: 0,
  finalJeopardyAnswerSec: 60,
  dailyDoubleMinWager: 5,
  dailyDoubleMaxFloor: 2000,
  finalJeopardyMinWager: 0,
  dailyDoubleCountJeopardy: 1,
  dailyDoubleCountDoubleJeopardy: 2,
} as const;

/**
 * Classic — Straight Peril: power-ups off, early buzzes ignored, negatives allowed.
 * The default; a create with this set sends no `houseRules` (behavior unchanged).
 */
const CLASSIC_BUNDLE: RulesState = {
  powerupCards: false,
  comebackBoosts: false,
  autoAwardCount: 0,
  startingCardCount: DEFAULT_STARTING_CARDS,
  earlyBuzzPolicy: 'ignore',
  earlyBuzzSmashLimit: 3,
  // Negatives are allowed in Classic (FR-4.7) — the engine default.
  allowNegativeScores: true,
  // #329 Timers/Wagers/Board at their defaults so an untouched drawer stays Classic.
  ...CONFIG_DEFAULTS,
};

/**
 * Game Night — the family bundle: power-up cards + comeback boosts on, and
 * scores floored at $0 (kinder for kids). Early buzz stays `ignore` — the smash-
 * lockout anti-cheat is an explicit Advanced opt-in, not a party-mode default.
 */
const GAME_NIGHT_BUNDLE: RulesState = {
  powerupCards: true,
  comebackBoosts: true,
  autoAwardCount: 3,
  startingCardCount: DEFAULT_STARTING_CARDS,
  earlyBuzzPolicy: 'ignore',
  earlyBuzzSmashLimit: 3,
  allowNegativeScores: false,
  // #329: Game Night shares the same Timers/Wagers/Board defaults (they aren't part of the
  // party-mode differentiation), so selecting Game Night doesn't blank those inputs.
  ...CONFIG_DEFAULTS,
};

function sameBundle(a: RulesState, b: RulesState): boolean {
  return (
    a.powerupCards === b.powerupCards &&
    a.comebackBoosts === b.comebackBoosts &&
    a.autoAwardCount === b.autoAwardCount &&
    a.startingCardCount === b.startingCardCount &&
    a.earlyBuzzPolicy === b.earlyBuzzPolicy &&
    a.earlyBuzzSmashLimit === b.earlyBuzzSmashLimit &&
    a.allowNegativeScores === b.allowNegativeScores &&
    // #329 knobs — MUST be compared, else a lone timer/wager/board change wouldn't flip
    // off Classic and `toHouseRules` would silently drop it.
    a.defaultTimerSec === b.defaultTimerSec &&
    a.answerWindowSec === b.answerWindowSec &&
    a.finalJeopardyWagerSec === b.finalJeopardyWagerSec &&
    a.finalJeopardyAnswerSec === b.finalJeopardyAnswerSec &&
    a.dailyDoubleMinWager === b.dailyDoubleMinWager &&
    a.dailyDoubleMaxFloor === b.dailyDoubleMaxFloor &&
    a.finalJeopardyMinWager === b.finalJeopardyMinWager &&
    a.dailyDoubleCountJeopardy === b.dailyDoubleCountJeopardy &&
    a.dailyDoubleCountDoubleJeopardy === b.dailyDoubleCountDoubleJeopardy
  );
}

type Preset = 'classic' | 'gamenight' | 'custom';

function selectedPreset(r: RulesState): Preset {
  if (sameBundle(r, CLASSIC_BUNDLE)) return 'classic';
  if (sameBundle(r, GAME_NIGHT_BUNDLE)) return 'gamenight';
  return 'custom';
}

/** The #329 knobs, emitted into the DTO only where they differ from the Classic default. */
function diffFromDefault(r: RulesState): Partial<HouseRules> {
  const keys = Object.keys(CONFIG_DEFAULTS) as (keyof typeof CONFIG_DEFAULTS)[];
  const out: Partial<HouseRules> = {};
  for (const k of keys) {
    if (r[k] !== CONFIG_DEFAULTS[k]) out[k] = r[k];
  }
  return out;
}

/**
 * Inverse of {@link diffFromDefault}: re-supply the #329 Timers/Wagers/Board knobs from a
 * DTO, falling back to the Classic default for any the DTO omits (`toHouseRules` sends only
 * the changed knobs). Keeps {@link fromHouseRules} lossless as `RulesState` grew these fields.
 */
function configFromHouseRules(hr: HouseRules): typeof CONFIG_DEFAULTS {
  const out: Record<string, number> = { ...CONFIG_DEFAULTS };
  for (const k of Object.keys(CONFIG_DEFAULTS)) {
    const v = (hr as Record<string, unknown>)[k];
    if (typeof v === 'number') out[k] = v;
  }
  return out as typeof CONFIG_DEFAULTS;
}

/**
 * Resolve the toggle set to the bounded DTO. Classic → `undefined` (send nothing).
 * The smash limit is only sent when the policy actually consults it.
 */
function toHouseRules(r: RulesState): HouseRules | undefined {
  if (sameBundle(r, CLASSIC_BUNDLE)) return undefined;
  return {
    powerups: r.powerupCards ? 'GAME_NIGHT' : 'CLASSIC',
    comebackBoosts: r.comebackBoosts,
    ...(r.powerupCards
      ? { autoAwardCount: r.autoAwardCount, startingCardCount: r.startingCardCount }
      : {}),
    earlyBuzzPolicy: r.earlyBuzzPolicy,
    ...(r.earlyBuzzPolicy === 'smash-lockout'
      ? { earlyBuzzSmashLimit: r.earlyBuzzSmashLimit }
      : {}),
    allowNegativeScores: r.allowNegativeScores,
    // #329: emit each Timers/Wagers/Board knob ONLY when it differs from the Classic
    // default, keeping the wire body minimal (the server clamps whatever arrives). A DD
    // count is sent only when changed, so an untouched Board leaves the pack/generator's
    // own placement intact (no relabel).
    ...diffFromDefault(r),
  };
}

/**
 * Inverse of {@link toHouseRules} (#328): expand a bounded {@link HouseRules} DTO back into
 * the full {@link RulesState} the chooser drives. Required so a saved preset (or the host's
 * `lastUsed` config) can be applied through the SAME `apply(...)` seam the built-in cards
 * use. The DTO omits the power-up-only fields when power-ups are off and the smash limit
 * unless smash-lockout, so this re-supplies those from `CLASSIC_BUNDLE`/the Game Night
 * defaults — the exact fields `toHouseRules` drops — making the round-trip lossless for the
 * fields the chooser actually exposes.
 */
function fromHouseRules(hr: HouseRules | undefined): RulesState {
  if (!hr) return CLASSIC_BUNDLE;
  const powerupCards = hr.powerups === 'GAME_NIGHT';
  return {
    powerupCards,
    // `comebackBoosts` only matters with power-ups; default to the Game Night bundle value.
    comebackBoosts: powerupCards ? (hr.comebackBoosts ?? true) : false,
    autoAwardCount:
      powerupCards && typeof hr.autoAwardCount === 'number'
        ? hr.autoAwardCount
        : CLASSIC_BUNDLE.autoAwardCount,
    startingCardCount:
      powerupCards && typeof hr.startingCardCount === 'number'
        ? hr.startingCardCount
        : DEFAULT_STARTING_CARDS,
    earlyBuzzPolicy: hr.earlyBuzzPolicy ?? CLASSIC_BUNDLE.earlyBuzzPolicy,
    earlyBuzzSmashLimit:
      hr.earlyBuzzPolicy === 'smash-lockout' && typeof hr.earlyBuzzSmashLimit === 'number'
        ? hr.earlyBuzzSmashLimit
        : CLASSIC_BUNDLE.earlyBuzzSmashLimit,
    allowNegativeScores: hr.allowNegativeScores ?? CLASSIC_BUNDLE.allowNegativeScores,
    ...configFromHouseRules(hr),
  };
}

/** A one-line human summary of a saved preset's config, for its card (#328). */
function summarizeRules(hr: HouseRules): string {
  const parts: string[] = [];
  if (hr.powerups === 'GAME_NIGHT') {
    parts.push('Power-ups on');
    if (typeof hr.autoAwardCount === 'number' && hr.autoAwardCount > 0) {
      parts.push(`${hr.autoAwardCount} auto-cards`);
    }
    if (hr.comebackBoosts === false) parts.push('no comebacks');
  } else {
    parts.push('Power-ups off');
  }
  if (hr.earlyBuzzPolicy === 'lockout') parts.push('forfeit on early buzz');
  else if (hr.earlyBuzzPolicy === 'smash-lockout') {
    parts.push(
      `smash-lockout${
        typeof hr.earlyBuzzSmashLimit === 'number'
          ? ` (limit ${hr.earlyBuzzSmashLimit})`
          : ''
      }`,
    );
  }
  parts.push(hr.allowNegativeScores === false ? '$0 floor' : 'negatives on');
  return parts.join(' · ');
}

interface HouseRulesChooserProps {
  /** Reports the resolved House Rules (or `undefined` for Classic) on every change. */
  onChange: (houseRules: HouseRules | undefined) => void;
  /**
   * The chosen host mode (#315). In `AUTO` the relocated "Rate clues" toggle in the
   * Hosting group is disabled + forced off (no human host to rate). Defaults to `HUMAN`.
   */
  hostMode?: HostMode;
  /**
   * Host-local "Rate clues" enablement (#201, D201.12), owned by `CreateGame` and
   * persisted to that host's browser only — NEVER folded into the `houseRules` DTO / the
   * wire. Passed down so the toggle can render inside the Advanced drawer's Hosting group.
   */
  rateClues?: boolean;
  /** Reports a rate-clues toggle up to `CreateGame` (which persists it to localStorage). */
  onRateCluesChange?: (enabled: boolean) => void;
  /**
   * The host's server-saved House Rules presets (#328, FR-19.1), account-scoped +
   * cross-device. Rendered as a third card group under the two built-ins; selecting one
   * applies its `rules`. Empty / omitted ⇒ only the built-ins show. Controlled by the
   * parent (`CreateGame`), which reconciles the list on save/delete.
   */
  savedPresets?: SavedRulePreset[];
  /**
   * The host's last-used config (#328, FR-19.3). When provided the chooser initializes to
   * it (once, on first load) instead of Classic. Undefined ⇒ Classic.
   */
  lastUsed?: HouseRules;
  /**
   * Whether the Save affordance is offered — true only for a registered host (saving is
   * server-side + registered-only, FR-12.7). A guest never sees the save box. Defaults false.
   */
  canSaveRules?: boolean;
  /**
   * Save the current custom rules under `name` (#328). Rejects (with a message) on the cap
   * or a validation failure — the chooser surfaces `err.message` inline. The parent performs
   * the API call and reconciles {@link savedPresets}.
   */
  onSavePreset?: (name: string, rules: HouseRules) => Promise<void>;
  /** Delete one saved preset by id (#328). The parent performs the API call + reconciles. */
  onDeletePreset?: (id: string) => Promise<void>;
}

export function HouseRulesChooser({
  onChange,
  hostMode = 'HUMAN',
  rateClues = false,
  onRateCluesChange,
  savedPresets = [],
  lastUsed,
  canSaveRules = false,
  onSavePreset,
  onDeletePreset,
}: HouseRulesChooserProps): React.JSX.Element {
  const [rules, setRules] = useState<RulesState>(() => fromHouseRules(lastUsed));
  const [advancedOpen, setAdvancedOpen] = useState(false);
  const rateCluesDisabled = hostMode === 'AUTO';

  // Single mutation seam: apply a patch, recompute, and report the DTO up.
  const apply = useCallback(
    (patch: Partial<RulesState>) => {
      setRules((prev) => {
        const next = { ...prev, ...patch };
        onChange(toHouseRules(next));
        return next;
      });
    },
    [onChange],
  );

  // Initialize to the host's last-used config once it arrives (#328, FR-19.3 / review N-2).
  // `CreateGame` fetches prefs on mount, so `lastUsed` typically lands AFTER first render —
  // the lazy `useState` initializer above can't see it. Apply it exactly once (guarded), and
  // only if the host hasn't already touched the toggles, so we never stomp a live edit.
  const lastUsedApplied = useRef(false);
  const touched = useRef(false);
  useEffect(() => {
    if (lastUsedApplied.current || touched.current || !lastUsed) return;
    lastUsedApplied.current = true;
    apply(fromHouseRules(lastUsed));
  }, [lastUsed, apply]);

  // Apply a full rules set (a built-in bundle or a saved/last-used preset) through the same
  // seam, and mark the surface touched so a late-arriving `lastUsed` no longer overrides it.
  const applyRules = useCallback(
    (next: RulesState) => {
      touched.current = true;
      apply(next);
    },
    [apply],
  );

  const preset = selectedPreset(rules);
  // Which saved preset (if any) matches the current toggle set — so its card reads selected.
  const activeSavedId = savedPresets.find((p) =>
    sameBundle(rules, fromHouseRules(p.rules)),
  )?.id;

  // Save affordance (nested in the Advanced drawer footer per the owner's steer): offered
  // only to a registered host, once they've tweaked to a Custom set. At the cap the form is
  // replaced by a delete-one message (no silent eviction).
  const atCap = savedPresets.length >= MAX_SAVED_RULE_PRESETS;
  const canOfferSave = canSaveRules && preset === 'custom' && Boolean(onSavePreset);
  const [saveName, setSaveName] = useState('');
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  const submitSave = useCallback(async () => {
    const dto = toHouseRules(rules);
    const trimmed = saveName.trim();
    if (!dto || trimmed === '' || !onSavePreset) return;
    setSaving(true);
    setSaveError(null);
    try {
      await onSavePreset(trimmed, dto);
      setSaveName('');
    } catch (err) {
      setSaveError(err instanceof Error ? err.message : 'Could not save.');
    } finally {
      setSaving(false);
    }
  }, [rules, saveName, onSavePreset]);

  return (
    <section className="house-rules" data-testid="house-rules">
      <h3 className="house-rules__heading">House Rules</h3>

      {/* Preset cards (mock 13) */}
      <div
        className="house-rules__presets"
        role="radiogroup"
        aria-label="House Rules preset"
      >
        <button
          type="button"
          role="radio"
          aria-checked={preset === 'classic'}
          data-testid="preset-classic"
          className={`house-rules__preset${preset === 'classic' ? ' is-selected' : ''}`}
          onClick={() => applyRules(CLASSIC_BUNDLE)}
        >
          <span className="house-rules__preset-title">Classic</span>
          <span className="house-rules__preset-desc muted">
            Straight Peril. Same clues, same rules for everyone — may the best player win.
          </span>
        </button>

        <button
          type="button"
          role="radio"
          aria-checked={preset === 'gamenight'}
          data-testid="preset-gamenight"
          className={`house-rules__preset${preset === 'gamenight' ? ' is-selected' : ''}`}
          onClick={() => applyRules(GAME_NIGHT_BUNDLE)}
        >
          <span className="house-rules__preset-title">🎉 Game Night</span>
          <span className="house-rules__preset-desc muted">
            Cards, comebacks, and a level playing field so the whole family stays in it
            &rsquo;til the end.
          </span>
        </button>
      </div>

      {/* Saved presets (#328): the host's own named configs, a third card group under the
          built-ins. Selecting one applies its rules through the same seam; the inline ✕
          deletes it. Omitted entirely when the host has none. */}
      {savedPresets.length > 0 && (
        <div className="house-rules__saved" data-testid="saved-presets">
          <p className="house-rules__saved-label muted">
            Your saved rules · {savedPresets.length} of {MAX_SAVED_RULE_PRESETS}
          </p>
          <div
            className="house-rules__presets"
            role="radiogroup"
            aria-label="Your saved House Rules"
          >
            {savedPresets.map((p) => (
              <div key={p.id} className="house-rules__saved-card-wrap">
                <button
                  type="button"
                  role="radio"
                  aria-checked={activeSavedId === p.id}
                  data-testid={`saved-preset-${p.id}`}
                  className={`house-rules__preset${
                    activeSavedId === p.id ? ' is-selected' : ''
                  }`}
                  onClick={() => applyRules(fromHouseRules(p.rules))}
                >
                  <span className="house-rules__preset-title">{p.name}</span>
                  <span className="house-rules__preset-desc muted">
                    {summarizeRules(p.rules)}
                  </span>
                </button>
                {onDeletePreset && (
                  <button
                    type="button"
                    className="house-rules__saved-delete"
                    data-testid={`delete-preset-${p.id}`}
                    aria-label={`Delete ${p.name} rules`}
                    onClick={() => void onDeletePreset(p.id)}
                  >
                    ✕
                  </button>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {preset === 'custom' && (
        <p className="house-rules__custom-note muted" data-testid="preset-custom-note">
          Custom — you&rsquo;ve tweaked the switches below.
        </p>
      )}

      {/* Advanced drawer (mock 18) */}
      <div className="house-rules__advanced">
        <button
          type="button"
          className="house-rules__advanced-toggle"
          data-testid="advanced-toggle"
          aria-expanded={advancedOpen}
          onClick={() => setAdvancedOpen((o) => !o)}
        >
          {advancedOpen ? '▾' : '▸'} Advanced
        </button>

        {advancedOpen && (
          <div className="house-rules__advanced-body" data-testid="advanced-body">
            {/* --- Power-ups --- */}
            <fieldset className="house-rules__group">
              <legend>Power-ups</legend>
              <label className="house-rules__row">
                <span>
                  Power-up cards
                  <span className="muted house-rules__hint">
                    {' '}
                    — Double Up, Sneak Peek, Steal, Brain Freeze, Snake Bite, Insurance,
                    Wager Boost, Tag-Team
                  </span>
                </span>
                <ToggleSwitch
                  testid="hr-powerup-cards"
                  checked={rules.powerupCards}
                  onChange={(checked) => apply({ powerupCards: checked })}
                />
              </label>
              <label className="house-rules__row">
                <span>
                  Comeback boosts
                  <span className="muted house-rules__hint">
                    {' '}
                    — trailing players draw cards more often
                  </span>
                </span>
                <ToggleSwitch
                  testid="hr-comeback-boosts"
                  checked={rules.comebackBoosts}
                  disabled={!rules.powerupCards}
                  onChange={(checked) => apply({ comebackBoosts: checked })}
                />
              </label>
              <label className="house-rules__row">
                <span>
                  Auto-awarded cards
                  <span className="muted house-rules__hint">
                    {' '}
                    — comeback cards handed to trailing players (some at the start, the
                    rest through the game)
                  </span>
                </span>
                <input
                  type="number"
                  min={0}
                  max={AUTO_AWARD_MAX}
                  data-testid="hr-auto-award"
                  value={rules.autoAwardCount}
                  disabled={!rules.powerupCards}
                  onChange={(e) => {
                    const n = Number(e.target.value);
                    apply({
                      autoAwardCount: Number.isFinite(n)
                        ? Math.min(AUTO_AWARD_MAX, Math.max(0, Math.floor(n)))
                        : rules.autoAwardCount,
                    });
                  }}
                />
              </label>
              <label className="house-rules__row">
                <span>
                  Starting cards per player
                  <span className="muted house-rules__hint">
                    {' '}
                    — how many random cards each player starts holding (0&ndash;8)
                  </span>
                </span>
                <input
                  type="number"
                  min={0}
                  max={STARTING_CARDS_MAX}
                  data-testid="hr-starting-cards"
                  value={rules.startingCardCount}
                  disabled={!rules.powerupCards}
                  onChange={(e) => {
                    const n = Number(e.target.value);
                    apply({
                      startingCardCount: Number.isFinite(n)
                        ? Math.min(STARTING_CARDS_MAX, Math.max(0, Math.floor(n)))
                        : rules.startingCardCount,
                    });
                  }}
                />
              </label>
            </fieldset>

            {/* --- Buzzer --- */}
            <fieldset className="house-rules__group">
              <legend>Buzzer</legend>
              <label className="house-rules__row">
                <span>Early buzz</span>
                <select
                  data-testid="hr-early-buzz"
                  value={rules.earlyBuzzPolicy}
                  onChange={(e) =>
                    apply({ earlyBuzzPolicy: e.target.value as EarlyBuzzPolicy })
                  }
                >
                  <option value="ignore">Ignore</option>
                  <option value="lockout">Forfeit on first</option>
                  <option value="smash-lockout">Smash-lockout</option>
                </select>
              </label>
              {rules.earlyBuzzPolicy === 'smash-lockout' && (
                <label className="house-rules__row">
                  <span>
                    Smash limit
                    <span className="muted house-rules__hint">
                      {' '}
                      — early taps allowed before lockout
                    </span>
                  </span>
                  <input
                    type="number"
                    min={1}
                    max={10}
                    data-testid="hr-smash-limit"
                    value={rules.earlyBuzzSmashLimit}
                    onChange={(e) => {
                      const n = Number(e.target.value);
                      apply({
                        earlyBuzzSmashLimit: Number.isFinite(n)
                          ? Math.min(10, Math.max(1, Math.floor(n)))
                          : rules.earlyBuzzSmashLimit,
                      });
                    }}
                  />
                </label>
              )}
            </fieldset>

            {/* --- Scoring --- */}
            <fieldset className="house-rules__group">
              <legend>Scoring</legend>
              <label className="house-rules__row">
                <span>
                  Scores can go negative
                  <span className="muted house-rules__hint">
                    {' '}
                    — off floors at $0 (kinder for kids)
                  </span>
                </span>
                <ToggleSwitch
                  testid="hr-allow-negative"
                  checked={rules.allowNegativeScores}
                  onChange={(checked) => apply({ allowNegativeScores: checked })}
                />
              </label>
            </fieldset>

            {/* --- Timers (#329, FR-9.12) --- */}
            <fieldset className="house-rules__group">
              <legend>Timers</legend>
              <NumberRow
                label="Buzz-in time"
                hint="seconds to buzz in on a clue"
                testid="hr-buzz-in-sec"
                value={rules.defaultTimerSec}
                min={TIMER_MIN}
                max={TIMER_MAX}
                onChange={(defaultTimerSec) => apply({ defaultTimerSec })}
              />
              <NumberRow
                label="Answer time"
                hint="seconds to answer once you've buzzed"
                testid="hr-answer-sec"
                value={rules.answerWindowSec}
                min={TIMER_MIN}
                max={TIMER_MAX}
                onChange={(answerWindowSec) => apply({ answerWindowSec })}
              />
              <NumberRow
                label="Final Peril wager time"
                hint="seconds to wager (0 = no limit)"
                testid="hr-final-wager-sec"
                value={rules.finalJeopardyWagerSec}
                min={0}
                max={FINAL_SEC_MAX}
                onChange={(finalJeopardyWagerSec) => apply({ finalJeopardyWagerSec })}
              />
              <NumberRow
                label="Final Peril answer time"
                hint="seconds to write the final response"
                testid="hr-final-answer-sec"
                value={rules.finalJeopardyAnswerSec}
                min={FINAL_ANSWER_MIN}
                max={FINAL_SEC_MAX}
                onChange={(finalJeopardyAnswerSec) => apply({ finalJeopardyAnswerSec })}
              />
            </fieldset>

            {/* --- Wagers (#329, FR-9.12) --- */}
            <fieldset className="house-rules__group">
              <legend>Wagers</legend>
              <NumberRow
                label="Hidden Peril min wager"
                hint="$ floor for a Hidden Peril wager"
                testid="hr-dd-min-wager"
                value={rules.dailyDoubleMinWager}
                min={0}
                max={WAGER_MAX}
                onChange={(dailyDoubleMinWager) => apply({ dailyDoubleMinWager })}
              />
              <NumberRow
                label="Hidden Peril max floor"
                hint="you may always wager up to this $"
                testid="hr-dd-max-floor"
                value={rules.dailyDoubleMaxFloor}
                min={0}
                max={WAGER_MAX}
                onChange={(dailyDoubleMaxFloor) => apply({ dailyDoubleMaxFloor })}
              />
              <NumberRow
                label="Final Peril min wager"
                hint="$ floor for a Final Peril wager (0 = stand pat)"
                testid="hr-final-min-wager"
                value={rules.finalJeopardyMinWager}
                min={0}
                max={WAGER_MAX}
                onChange={(finalJeopardyMinWager) => apply({ finalJeopardyMinWager })}
              />
            </fieldset>

            {/* --- Board (#329, FR-5.1) — per-game Hidden Peril count. Applied at game
                creation by relabeling arbitrary clues; no regeneration (D-329.4). --- */}
            <fieldset className="house-rules__group">
              <legend>Board</legend>
              <NumberRow
                label="Hidden Perils — Peril round"
                hint="how many in round 1 (0–30)"
                testid="hr-dd-count-jeopardy"
                value={rules.dailyDoubleCountJeopardy}
                min={0}
                max={DD_COUNT_MAX}
                onChange={(dailyDoubleCountJeopardy) =>
                  apply({ dailyDoubleCountJeopardy })
                }
              />
              <NumberRow
                label="Hidden Perils — Double Peril round"
                hint="how many in round 2 (0–30)"
                testid="hr-dd-count-double"
                value={rules.dailyDoubleCountDoubleJeopardy}
                min={0}
                max={DD_COUNT_MAX}
                onChange={(dailyDoubleCountDoubleJeopardy) =>
                  apply({ dailyDoubleCountDoubleJeopardy })
                }
              />
            </fieldset>

            {/* --- Hosting (#315, DI-315.2) --- */}
            {/* The host-local "Rate clues" toggle (#201, D201.12), relocated here from its
                former stray top-level slot into its own Hosting group (peer of Power-ups /
                Buzzer / Scoring). It stays host-client-local — persisted to the host's
                browser only, NEVER part of the houseRules DTO / the wire. In auto-host mode
                there is no human host to rate, so it is disabled + forced off + hinted. */}
            <fieldset className="house-rules__group">
              <legend>Hosting</legend>
              <label className="house-rules__row" data-testid="rate-clues-toggle">
                <span>
                  Rate clues as I host
                  <span className="muted house-rules__hint">
                    {rateCluesDisabled
                      ? ' — not available in auto-host mode'
                      : ' — a good/bad panel below the board; helps improve future clues'}
                  </span>
                </span>
                <ToggleSwitch
                  testid="rate-clues-checkbox"
                  checked={rateCluesDisabled ? false : rateClues}
                  disabled={rateCluesDisabled}
                  onChange={(checked) => onRateCluesChange?.(checked)}
                />
              </label>
            </fieldset>

            {/* --- Save these rules (#328, owner steer: nested in the Advanced footer) ---
                Shown once the host has tweaked to a Custom set (and only for a registered
                host). At the cap the form is replaced by a delete-one message. */}
            {canOfferSave && (
              <div className="house-rules__save" data-testid="save-rules">
                {atCap ? (
                  <p className="house-rules__save-cap muted" data-testid="save-cap-note">
                    You&rsquo;ve saved {MAX_SAVED_RULE_PRESETS} configurations (the max).
                    Delete one to save another.
                  </p>
                ) : (
                  <>
                    <label className="house-rules__save-label">
                      Save these rules to reuse next time?
                      <input
                        type="text"
                        className="house-rules__save-name"
                        data-testid="save-rules-name"
                        placeholder="Name this configuration"
                        maxLength={RULE_PRESET_NAME_MAX_LEN}
                        value={saveName}
                        disabled={saving}
                        onChange={(e) => setSaveName(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            e.preventDefault();
                            void submitSave();
                          }
                        }}
                      />
                    </label>
                    <div className="house-rules__save-actions">
                      <button
                        type="button"
                        className="btn btn--primary"
                        data-testid="save-rules-submit"
                        disabled={saving || saveName.trim() === ''}
                        onClick={() => void submitSave()}
                      >
                        {saving ? 'Saving…' : 'Save'}
                      </button>
                      {saveName !== '' && (
                        <button
                          type="button"
                          className="btn btn--ghost"
                          data-testid="save-rules-cancel"
                          disabled={saving}
                          onClick={() => {
                            setSaveName('');
                            setSaveError(null);
                          }}
                        >
                          Cancel
                        </button>
                      )}
                    </div>
                    {saveError && (
                      <p
                        className="house-rules__save-error"
                        role="alert"
                        data-testid="save-rules-error"
                      >
                        {saveError}
                      </p>
                    )}
                  </>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </section>
  );
}
